import { formatAcfrCategory } from '~support-at-home/lib/acfrCategory';

import { Column, ColumnPickerGroup } from '~components/table-view';
import { getDefaultRenderReadOnly } from '~components/table-view/columnBuilder';

import { ExportColumn, ExportColumnType } from '~support-at-home/models/exportColumns';

import { ColumnSpec } from '~types/columnSpec';

const RIGHT_ALIGN = { header: 'cell-align-right', column: 'cell-align-right' };

// Display order for the ACFR Items table. Presentation only: the backend's specs array is the
// source of which columns exist. A key not listed here is appended rather than dropped, so a
// newly registered column still appears without a frontend change.
const COLUMN_ORDER = [
  'source',
  'reference',
  'claim_reference',
  'recon_reference',
  'statement_reference',
  'service_date',
  'claims_month',
  'statement_month',
  'entitlement_type',
  'budget_type_code',
  'acfr_category',
  'commonwealth_amount',
  'participant_contribution',
];

export const COLUMN_GROUPS: ColumnPickerGroup[] = [
  { title: 'ACFR derivation', keys: ['acfr_category', 'budget_type_code', 'commonwealth_amount', 'participant_contribution'] },
  { title: 'Entitlement', keys: ['source', 'entitlement_type'] },
  { title: 'Identifiers', keys: ['reference', 'claim_reference', 'recon_reference', 'statement_reference'] },
  { title: 'Dates', keys: ['service_date', 'claims_month', 'statement_month'] },
];

const SOURCE_LABELS: Record<string, string> = {
  claim: 'Reconciled claim',
  invoice: 'Unclaimed invoice',
};

const ENTITLEMENT_LABELS: Record<string, string> = {
  current: 'Current',
  amendment: 'Amendment',
  adjustment: 'Adjustment',
};

type CellRenderer = NonNullable<Column['renderReadOnly']>;

/**
 * The shared date renderer prints 'N/A' for a null date. On an unclaimed supplier invoice row
 * (`source === 'invoice'`) that reads as a missing month, when the row has no claim and no
 * statement to carry one at all — which is why its Claim ID, Recon ID, Statement ID and
 * Entitlement Type cells are already blank, and why the CSV export leaves these two blank too.
 *
 * Blanked for invoice rows only. A claim row with no statement yet keeps 'N/A', matching the
 * Claim Items tab, where the absent statement really is something still to come.
 */
const renderDateCell: CellRenderer = getDefaultRenderReadOnly('date');
const renderBlankCell: CellRenderer = getDefaultRenderReadOnly();

const monthCellRender: CellRenderer = (value, row) =>
  row.source === 'invoice' ? renderBlankCell(null, row) : renderDateCell(value, row);

/**
 * Per-column rendering that can't come from the specs contract. Keyed by column key; a spec key
 * with no entry renders as plain read-only text.
 *
 * Note there is deliberately no combined total column: Commonwealth Amount and Participant
 * Contribution stay separate, and the participant figure lands on one row of a split item only,
 * so a derived per-row total would invite a sum that double counts.
 */
const columnBehaviour: Record<string, Partial<Column>> = {
  source: { deriveValue: (row) => SOURCE_LABELS[String(row.source)] ?? row.source },
  entitlement_type: {
    deriveValue: (row) => (row.entitlement_type ? (ENTITLEMENT_LABELS[String(row.entitlement_type)] ?? row.entitlement_type) : null),
  },
  service_date: { type: 'date' },
  claims_month: { type: 'date', renderReadOnly: monthCellRender },
  statement_month: { type: 'date', renderReadOnly: monthCellRender },
  acfr_category: { deriveValue: (row) => formatAcfrCategory(row.acfr_category) },
  commonwealth_amount: { type: 'currency', classNames: RIGHT_ALIGN },
  participant_contribution: {
    type: 'currency',
    classNames: RIGHT_ALIGN,
    caption:
      'Attributed in full to the first budget entry outside Clinical and Care management; other entries on a split item show $0.00.',
  },
};

const orderColumnSpecs = (columnSpecs: ColumnSpec[]): ColumnSpec[] => {
  const specsByKey = new Map(columnSpecs.map((spec) => [spec.key, spec]));
  const ordered = COLUMN_ORDER.map((key) => specsByKey.get(key)).filter((spec): spec is ColumnSpec => Boolean(spec));
  const remaining = columnSpecs.filter((spec) => !COLUMN_ORDER.includes(spec.key));
  return [...ordered, ...remaining];
};

export const columnsFactory = (columnSpecs: ColumnSpec[]): Column[] =>
  orderColumnSpecs(columnSpecs).map(
    (spec): Column => ({
      id: spec.key,
      header: spec.label,
      type: 'readonly',
      showColumn: spec.visible,
      ...columnBehaviour[spec.key],
    }),
  );

// ── CSV export column contract ────────────────────────────────────────────────
// The document engine renders whatever { key, label, type } list the export job carries, in
// that order, so the export follows what is on screen. Currency and date types are inferred
// from the key on the generator side too, but sending them explicitly keeps the contract
// readable in the job payload. The ExportColumn shape itself is shared with the other SaH tabs
// that export (see ~support-at-home/models/exportColumns); only the derivation below is local,
// because this tab's columns are virtual and carry no model attribute to read a type from.

const exportTypeFor = (key: string): ExportColumnType => {
  if (key === 'acfr_category') return 'acfr_category';
  if (key === 'commonwealth_amount' || key === 'participant_contribution') return 'currency';
  if (key.endsWith('_date') || key.endsWith('_month')) return 'date';
  return 'text';
};

export const exportColumnsFor = (columns: Column[]): ExportColumn[] =>
  columns
    .filter((column) => column.showColumn !== false)
    .map((column) => ({
      key: String(column.id),
      label: String(column.header ?? column.id),
      type: exportTypeFor(String(column.id)),
    }));
